import {
  Box,
  Checkbox,
  Typography,
  Portal,
  Fab,
  useMediaQuery,
  useTheme,
  Tooltip,
  Paper,
  alpha,
  Container,
} from "@mui/material";
// import Grid from "@mui/material/Grid";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { ExtractedInfoPage } from "../ExtractedInfo/ExtractedInfoPage";
import { useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import type { JSONSchema7 } from "json-schema";
import { LoadingButton } from "@mui/lab";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { ReportSubmitModal } from "../../modals/ReportSubmitModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React from "react";
import { FieldMapping } from "../../config/fieldMapping";
import { rmsClient } from "../../api/clients";
import { usePageHeader } from "../../context/LayoutContext";
import { useAuthContext } from "../../context/AuthContext";
import { useTnCaseNumberInputEnabled } from "../../hooks/useTnCaseNumberInputEnabled";
import FactCheckIcon from "@mui/icons-material/FactCheck";

type FinalEditsState = {
  modelData: EforceIncidentModel;
  showRMSCommit: boolean;
  sparseInfo: unknown;
  jsonSchema: JSONSchema7;
  schemaMapping: Record<string, FieldMapping>;
};

export const FinalEditsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reportID } = useParams(); // top-level hook use
  const theme = useTheme();
  // Set page header in app bar
  usePageHeader({
    title: "Final Edits",
    subtitle: "Review and confirm your report before submission",
    icon: <FactCheckIcon />,
  });

  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { organizationId } = useAuthContext();
  const caseNumberInputEnabled = useTnCaseNumberInputEnabled();

  const [confirmInfo, setConfirmInfo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [allEasterEggsFound, setAllEasterEggsFound] = useState(false);
  const [easterEggErrorMessage, setEasterEggErrorMessage] = useState("");
  const snackbarPosition = useSnackbarPosition();

  const state = location.state as FinalEditsState | undefined;

  const incidentModelRef = useRef<EforceIncidentModel | null>(
    state?.modelData || null
  );
  const handleSubmitFunction = useRef<(caseNumber?: string) => void>(() => {});

  // References for tracking easter eggs found in each narrative
  const narrativeEasterEggRefs = useRef<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>({});

  /**
   * @name checkAllEasterEggsFound
   *
   * @description Checks if all narratives' easter eggs have been found
   */
  const checkAllEasterEggsFound = () => {
    const model = incidentModelRef.current;
    if (!model || !model.Narratives || model.Narratives.length === 0) {
      setAllEasterEggsFound(true);
      return;
    }

    const refs = Object.values(narrativeEasterEggRefs.current);
    const expectedCount = model.Narratives.length;
    const allFound =
      refs.length === expectedCount &&
      refs.every((ref) => ref?.current === true);

    setAllEasterEggsFound(allFound);

    setAllEasterEggsFound(allFound);
  };

  /**
   * @name handleEasterEggFound
   *
   * @description Callback when an easter egg is found - rechecks if all are found
   */
  const handleEasterEggFound = () => {
    checkAllEasterEggsFound();
  };

  // Check if there are any narratives when component mounts
  React.useEffect(() => {
    checkAllEasterEggsFound();
  }, []);

  const hasRequiredState =
    !!state?.modelData &&
    typeof state?.showRMSCommit === "boolean" &&
    !!state?.sparseInfo;

  // Redirect immediately (before rendering the rest) if state is missing
  if (!hasRequiredState) {
    // Replace with your actual edit route; using /edit here as an example
    const to = `/edit-section/${reportID ?? "/"}`;
    return (
      <Navigate to={to} replace state={{ error: "No data was provided" }} />
    );
  }

  const handleConfirmCheckboxClick = () => {
    if (!allEasterEggsFound) {
      setEasterEggErrorMessage(
        "Please find and delete all easter eggs in the narratives before you can confirm and submit the report."
      );
      return;
    }
    setConfirmInfo((prev) => !prev);
  };

  const handleRMSCommit = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    const incident = incidentModelRef.current;
    if (!incident) {
      setErrorMessage("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      const res = await rmsClient.post("/report/rms-commit", {
        reportId: parseInt(reportID!),
        ...(caseNumber ? { caseNumber } : {}),
      });
      const eventNumber = res.data.eventNumber;
      navigate("/confirmation/" + eventNumber, {
        state: { reportID },
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "An error occurred while submitting the report to the RMS servers. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitClick = (submitFunction: (caseNumber?: string) => void) => {
    if (isLoading) return;
    handleSubmitFunction.current = submitFunction;
    setOpenConfirmation(true);
  };

  const handleSignReport = async (caseNumber?: string) => {
    if (isLoading) return;
    setOpenConfirmation(false);
    const incident = incidentModelRef.current;
    if (!incident) {
      setErrorMessage("No incident data available");
      return;
    }
    setIsLoading(true);
    try {
      await rmsClient.post("/report/sign", {
        reportId: reportID!,
        ...(caseNumber ? { caseNumber } : {}),
      });
      navigate("/confirmation", {
        state: { reportID },
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "An error occurred while submitting the report to the RMS servers. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const memoizedSchemaMapping = useMemo(() => {
    // update schema mapping to have NarrativeViewWidget for narratives
    // Deep clone to avoid mutating the original schema mapping
    let newSchemaMapping = JSON.parse(JSON.stringify(state.schemaMapping));
    if (newSchemaMapping["Narratives"]?.properties?.["Text"]) {
      // schema mapping for eforce
      newSchemaMapping["Narratives"].properties["Text"].widget =
        "NarrativeViewWidget";
    } else if (newSchemaMapping["Narratives"]?.properties?.["Narrative"]) {
      // fallback schema mapping for IncidentUS (genericSchemaMapping)
      newSchemaMapping["Narratives"].properties["Narrative"].widget =
        "NarrativeViewWidget";
    }
    return newSchemaMapping;
  }, [state.schemaMapping]);

  const isRMSCommit = handleSubmitFunction.current === handleRMSCommit;

  return (
    <>
      {/* Page Header Removed */}
      {/* <Container ... /> */}

      <Portal>
        <Tooltip title="Return to editing page">
          <Fab
            variant={isLgUp ? "extended" : "circular"}
            onClick={() => navigate(-1)}
            disabled={isLoading}
            sx={{
              position: "fixed",
              top: isLgUp ? 70 : 130,
              right: 16,
              zIndex: 1000,
              bgcolor: theme.palette.primary.main,
              color: "white",
              border: "2px solid white",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            <ArrowBackIcon />
            {isLgUp && "Back to Edit"}
          </Fab>
        </Tooltip>
      </Portal>

      <ReportSubmitModal
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleSubmitReport={handleSubmitFunction.current}
        isRMSCommit={isRMSCommit}
        requireCaseNumber={caseNumberInputEnabled}
      />
      <SnackBarMessage
        message={errorMessage}
        mode="error"
        handleClose={() => setErrorMessage("")}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />
      <SnackBarMessage
        message={easterEggErrorMessage}
        mode="error"
        handleClose={() => setEasterEggErrorMessage("")}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />

      {/* Easter Egg Warning Banner */}
      {!allEasterEggsFound && (
        <Container maxWidth="xl" sx={{ mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.5rem",
                lineHeight: 1,
              }}
            >
              ⚠️
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.warning.dark,
                  mb: 0.5,
                }}
              >
                Action Required: Find and Remove Easter Eggs
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Before you can confirm and submit this report, you must find and
                delete the hidden "easter egg" phrases in each narrative
                section. These are nonsensical phrases highlighted in orange.
                Click on the easter egg text and press{" "}
                <strong>Backspace</strong> or <strong>Delete</strong> to remove
                it.
              </Typography>
            </Box>
          </Paper>
        </Container>
      )}

      <ExtractedInfoPage
        isFinalReview
        incidentModelRef={incidentModelRef}
        narrativeEasterEggRefs={narrativeEasterEggRefs}
        onEasterEggFound={handleEasterEggFound}
        jsonSchema={state.jsonSchema}
        schemaMapping={memoizedSchemaMapping}
        stationId={organizationId ?? undefined}
      />

      {/* Confirmation and Submit Section */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Confirmation Checkbox */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              cursor: !allEasterEggsFound ? "not-allowed" : "pointer",
              p: 2,
              borderRadius: 2,
              bgcolor: confirmInfo
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${
                confirmInfo
                  ? alpha(theme.palette.success.main, 0.3)
                  : alpha(theme.palette.divider, 0.1)
              }`,
              transition: "all 0.2s ease-in-out",
              width: "100%",
              maxWidth: 700,
              "&:hover": {
                bgcolor: confirmInfo
                  ? alpha(theme.palette.success.main, 0.12)
                  : alpha(theme.palette.primary.main, 0.08),
              },
            }}
            onClick={() => {
              if (!allEasterEggsFound) {
                setEasterEggErrorMessage(
                  "Please find and delete all easter eggs in the narratives before you can confirm and submit the report."
                );
              }
            }}
          >
            <Checkbox
              checked={confirmInfo}
              onChange={handleConfirmCheckboxClick}
              disabled={!allEasterEggsFound}
              sx={{
                color: theme.palette.primary.main,
                "&.Mui-checked": {
                  color: theme.palette.success.main,
                },
                "& .MuiSvgIcon-root": {
                  fontSize: 28,
                },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 500,
                color: confirmInfo
                  ? theme.palette.success.dark
                  : theme.palette.text.primary,
              }}
            >
              I confirm that all the information on this page is accurate to my
              knowledge.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 4,
              width: "100%",
            }}
          >
            {state.showRMSCommit && (
              <>
                <LoadingButton
                  onClick={() => handleSubmitClick(handleRMSCommit)}
                  variant="contained"
                  disabled={
                    !confirmInfo ||
                    isLoading ||
                    !incidentModelRef.current ||
                    !allEasterEggsFound
                  }
                  loading={isLoading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.35
                    )}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(
                        theme.palette.primary.main,
                        0.45
                      )}`,
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                      boxShadow: "none",
                    },
                  }}
                >
                  Sign Report & Commit to RMS
                </LoadingButton>
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  or
                </Typography>
              </>
            )}

            <LoadingButton
              onClick={() => handleSubmitClick(handleSignReport)}
              variant={state.showRMSCommit ? "outlined" : "contained"}
              disabled={
                !confirmInfo ||
                isLoading ||
                !incidentModelRef.current ||
                !allEasterEggsFound
              }
              loading={isLoading}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: "0.95rem",
                textTransform: "none",
                ...(state.showRMSCommit
                  ? {
                      borderWidth: 2,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderWidth: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }
                  : {
                      boxShadow: `0 4px 14px ${alpha(
                        theme.palette.primary.main,
                        0.35
                      )}`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: `0 6px 20px ${alpha(
                          theme.palette.primary.main,
                          0.45
                        )}`,
                        transform: "translateY(-2px)",
                      },
                    }),
                "&:disabled": {
                  background: state.showRMSCommit
                    ? "transparent"
                    : theme.palette.grey[300],
                  color: theme.palette.grey[500],
                  boxShadow: "none",
                  borderColor: theme.palette.grey[300],
                },
              }}
            >
              Sign Report & Download
            </LoadingButton>
          </Box>
        </Paper>
      </Container>
    </>
  );
};
